import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  LineChart, ArrowRight, Calendar, Sparkles, Repeat, TrendingUp, TrendingDown,
} from "lucide-react";
import { api, formatMXN } from "../lib/api";
import { useScope } from "../context/ScopeContext";
import ScreenHeader from "../components/layout/ScreenHeader";
import ScopeToggle from "../components/ScopeToggle";

const MONTH_ES_SHORT = [
  "ene", "feb", "mar", "abr", "may", "jun",
  "jul", "ago", "sep", "oct", "nov", "dic",
];

export default function Proyeccion() {
  const { scope } = useScope();
  const [summary, setSummary] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, u] = await Promise.all([
        api.get("/dashboard/summary", { params: { scope } }),
        api.get("/recurrences/upcoming", { params: { days: 30, scope } }),
      ]);
      setSummary(s.data);
      setUpcoming(u.data);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    load();
    const onChange = () => load();
    window.addEventListener("fp:tx-created", onChange);
    window.addEventListener("fp:rec-changed", onChange);
    return () => {
      window.removeEventListener("fp:tx-created", onChange);
      window.removeEventListener("fp:rec-changed", onChange);
    };
  }, [load]);

  const items = [
    { key: "7d", label: "En 7 días", days: 7, data: summary?.projections?.["7d"] },
    { key: "15d", label: "En 15 días", days: 15, data: summary?.projections?.["15d"] },
    { key: "eom", label: "A fin de mes", days: null, data: summary?.projections?.eom, highlight: true },
  ];

  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return `${d.getDate()} ${MONTH_ES_SHORT[d.getMonth()]}`;
    } catch {
      return "";
    }
  };

  return (
    <div data-testid="proyeccion-screen">
      <ScreenHeader
        eyebrow={scope === "shared" ? "Su futuro cercano" : "Tu futuro cercano"}
        title="Proyección"
        subtitle={scope === "shared"
          ? "Cómo se vería el dinero común considerando gastos fijos e ingresos recurrentes compartidos."
          : "Cómo se vería tu saldo considerando gastos fijos e ingresos recurrentes."}
      />

      <div className="px-6 mb-3">
        <ScopeToggle />
      </div>

      {/* Current balance */}
      <div className="px-6">
        <div className="rounded-3xl bg-white border border-line/70 shadow-card p-6">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700">
            {scope === "shared" ? "Dinero común hoy" : "Saldo real hoy"}
          </p>
          <p className="mt-1 font-display text-3xl font-extrabold tabular">
            {loading ? "…" : formatMXN(summary?.real_balance ?? 0)}
          </p>
        </div>
      </div>

      {/* Projection list */}
      <div className="px-6 mt-6 space-y-3" data-testid="projections-list">
        {items.map((it) => {
          const value = it.data?.projected_balance ?? 0;
          const incoming = it.data?.incoming ?? 0;
          const outgoing = it.data?.outgoing ?? 0;
          const hits = it.data?.hits_count ?? 0;
          return (
            <div
              key={it.key}
              data-testid={`proyeccion-card-${it.key}`}
              className={`rounded-3xl p-5 border transition-all hover:-translate-y-0.5 ${
                it.highlight
                  ? "bg-sage-900 text-bone border-sage-900 shadow-card"
                  : "bg-white border-line/70 shadow-card"
              }`}
            >
              <div className="flex items-center gap-4">
                <div
                  className={`h-12 w-12 rounded-2xl flex items-center justify-center shrink-0 ${
                    it.highlight ? "bg-bone/15 text-bone" : "bg-sage-50 text-sage-700"
                  }`}
                >
                  <Calendar size={20} strokeWidth={1.8} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-bold uppercase tracking-[0.16em] ${it.highlight ? "text-bone/80" : "text-sage-700"}`}>
                    {it.label}
                  </p>
                  <p className={`font-display font-extrabold text-2xl tabular ${it.highlight ? "text-bone" : "text-ink"}`}>
                    {loading ? "…" : formatMXN(value)}
                  </p>
                </div>
                <ArrowRight size={18} className={it.highlight ? "text-bone/70" : "text-soft"} />
              </div>

              {/* Mini breakdown */}
              <div className={`mt-4 grid grid-cols-3 gap-2 text-xs ${it.highlight ? "text-bone/85" : "text-muted2"}`}>
                <div>
                  <p className="uppercase tracking-wider font-bold opacity-80">Entran</p>
                  <p className="font-semibold tabular">+ {formatMXN(incoming)}</p>
                </div>
                <div>
                  <p className="uppercase tracking-wider font-bold opacity-80">Salen</p>
                  <p className="font-semibold tabular">− {formatMXN(outgoing)}</p>
                </div>
                <div>
                  <p className="uppercase tracking-wider font-bold opacity-80">Eventos</p>
                  <p className="font-semibold tabular">{hits}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Upcoming recurrences */}
      <div className="px-6 mt-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-bold flex items-center gap-2">
            <Repeat size={16} className="text-sage-700" /> Próximos 30 días
          </h3>
          <Link
            to="/recurrencias"
            data-testid="proyeccion-manage-link"
            className="text-xs font-bold uppercase tracking-wider text-sage-700 inline-flex items-center gap-1 hover:text-sage-900"
          >
            Gestionar <ArrowRight size={12} />
          </Link>
        </div>
        <div className="rounded-3xl bg-white border border-line/70 shadow-card overflow-hidden">
          {loading ? (
            <p className="text-soft text-sm text-center py-8">Cargando…</p>
          ) : upcoming.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-sm text-muted2">
                No tienes gastos fijos ni ingresos recurrentes activos.
              </p>
              <Link
                to="/recurrencias"
                data-testid="proyeccion-empty-cta"
                className="mt-4 inline-block bg-sage-900 text-bone rounded-xl px-4 py-2 text-sm font-bold"
              >
                Agregar un gasto fijo
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-line/70">
              {upcoming.map((u, idx) => (
                <li
                  key={`${u.recurrence_id}-${idx}`}
                  data-testid={`upcoming-${idx}`}
                  className="flex items-center gap-3 px-5 py-3.5"
                >
                  <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${
                    u.type === "ingreso"
                      ? "bg-sage-50 text-sage-700"
                      : "bg-bone text-ink"
                  }`}>
                    {u.type === "ingreso" ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-ink truncate">{u.name}</p>
                    <p className="text-xs text-soft">{formatDate(u.date)}</p>
                  </div>
                  <p className={`font-display font-bold tabular ${
                    u.type === "ingreso" ? "text-sage-700" : "text-ink"
                  }`}>
                    {u.type === "ingreso" ? "+" : "−"} {formatMXN(u.amount)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Info card */}
      <div className="px-6 mt-8">
        <div className="rounded-3xl bg-sage-50 border border-sage-100 p-5">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-xl bg-white text-sage-700 flex items-center justify-center shrink-0">
              <Sparkles size={16} />
            </div>
            <div>
              <p className="font-display font-bold text-ink">¿Cómo se calcula?</p>
              <p className="text-sm text-muted2 leading-relaxed mt-1">
                Partimos de tu saldo real de hoy y sumamos o restamos los gastos fijos e ingresos recurrentes activos que caen dentro del periodo.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Placeholder chart slot */}
      <div className="px-6 mt-6">
        <div
          className="rounded-3xl border-2 border-dashed border-line p-8 flex flex-col items-center text-center text-soft"
          data-testid="proyeccion-chart-placeholder"
        >
          <LineChart size={28} className="text-sage-200 mb-2" />
          <p className="text-sm">Gráfico de flujo (próxima fase)</p>
        </div>
      </div>
    </div>
  );
}
